import Sortable from "sortablejs";
import { initAdminFormControls } from "../global/initAdminFormControls";

/**
 * 페이지 자유 컨텐츠 블록 빌더.
 *
 * - [data-add-block="text|textarea|editor|file"] 버튼 → 해당 <template> 클론 후 캔버스에 추가.
 *   클론 HTML 의 __INDEX__ 를 단조 증가 인덱스로 치환(삭제해도 재사용 안 함 → 충돌 없음).
 * - 추가된 블록의 셀렉트·에디터·업로더는 initAdminFormControls(scope) 로 초기화.
 * - 드래그(좌측 손잡이)로 순서 변경. 별도 저장 없이 폼 제출 시 DOM 순서대로 서버가 sort_order 부여.
 * - 파일 블록의 숨은 attachment_id input 은 재렌더 없이 노드 통째로 이동하므로 유실되지 않는다.
 */
class PageBlockBuilder {
    readonly #root: HTMLElement;
    readonly #canvas: HTMLElement | null;
    readonly #empty: HTMLElement | null;
    #next: number;

    constructor(root: HTMLElement) {
        this.#root = root;
        this.#canvas = root.querySelector<HTMLElement>("[data-page-blocks-canvas]");
        this.#empty = root.querySelector<HTMLElement>("[data-page-blocks-empty]");
        this.#next = parseInt(root.dataset.nextIndex ?? "0", 10) || 0;
    }

    init(): void {
        if (!this.#canvas) return;
        const canvas = this.#canvas;

        this.#root.querySelectorAll<HTMLElement>("[data-add-block]").forEach((btn) => {
            btn.addEventListener("click", () => this.#add(btn.dataset.addBlock ?? "text"));
        });

        canvas.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            const removeBtn = target.closest<HTMLElement>("[data-block-remove]");
            if (removeBtn) {
                removeBtn.closest(".admin-page-block")?.remove();
                this.#updateEmpty();
            }
        });

        Sortable.create(canvas, {
            animation: 150,
            handle: "[data-block-handle]",
            draggable: ".admin-page-block",
            ghostClass: "admin-page-block--ghost",
        });

        this.#updateEmpty();
    }

    #add(type: string): void {
        const tpl =
            this.#root.querySelector<HTMLTemplateElement>(`[data-block-template="${type}"]`) ??
            document.querySelector<HTMLTemplateElement>(`[data-block-template="${type}"]`);
        if (!tpl || !this.#canvas) return;

        const idx = this.#next++;
        const html = tpl.innerHTML.replace(/__INDEX__/g, String(idx)).trim();
        const tmp = document.createElement("div");
        tmp.innerHTML = html;
        const node = tmp.firstElementChild as HTMLElement | null;
        if (!node) return;

        this.#canvas.appendChild(node);
        // 새 블록의 셀렉트·언어탭·에디터·업로더 초기화 (모달 주입과 동일 경로).
        initAdminFormControls(node);
        this.#updateEmpty();
        node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }

    #updateEmpty(): void {
        if (!this.#empty || !this.#canvas) return;
        this.#empty.hidden = this.#canvas.querySelector(".admin-page-block") !== null;
    }
}

interface BuilderRoot extends HTMLElement {
    _pageBlockBuilderInited?: boolean;
}

export function initPageBlockBuilder(): void {
    document.querySelectorAll<BuilderRoot>("[data-page-block-builder]").forEach((el) => {
        if (el._pageBlockBuilderInited) return;
        el._pageBlockBuilderInited = true;
        new PageBlockBuilder(el).init();
    });
}
